import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import BrandSelect from '../BrandSelect';
import ContactSelect from '../ContactSelect';
import PitchTalentPicker from './PitchTalentPicker';
import { useDeals } from '../../context/DataContext';
import { usePitches } from '../../context/PitchesContext';

const QuickAddPitch = ({ open, onOpenChange, defaultTalentId = null, defaultTitle = null, onSaved }) => {
  const { addPitch, updatePitch, pitches } = usePitches();
  const { brands, talent } = useDeals();
  const [brandId, setBrandId] = useState('');
  const [contactId, setContactId] = useState('');
  const [talentIds, setTalentIds] = useState(defaultTalentId ? [defaultTalentId] : []);
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setBrandId(''); setContactId(''); setTalentIds(defaultTalentId ? [defaultTalentId] : []);
      setTitle(defaultTitle || ''); setTitleTouched(false); setError(null);
    }
  }, [open, defaultTalentId, defaultTitle]);

  const selectedBrand = useMemo(() => brands.find((b) => b.id === brandId), [brands, brandId]);
  const selectedTalentNames = useMemo(() => talentIds.map((id) => talent.find((t) => t.user_id === id)?.name).filter(Boolean), [talentIds, talent]);

  // Picking a brand + talent auto-writes "Brand × Talent" into the title, matching how
  // every other pitch in the app is named — but stops touching it the moment the manager
  // types into the field themselves (e.g. to keep the originally pasted bubble text).
  useEffect(() => {
    if (titleTouched) return;
    if (selectedBrand && selectedTalentNames.length) setTitle(`${selectedBrand.name} × ${selectedTalentNames.join(', ')}`);
    else if (selectedBrand) setTitle(`${selectedBrand.name} pitch`);
  }, [selectedBrand, selectedTalentNames, titleTouched]);

  const canSave = !saving;

  // A brand only ever has ONE pitch row per talent in the tracker — if one already exists
  // for the picked (brand, talent), log this attempt as a note on it instead of duplicating.
  const findExisting = (tid) => pitches.find((p) => p.brand_id === brandId
    && (p.talent_id === tid || (Array.isArray(p.talent_ids) && p.talent_ids.includes(tid))));

  const logAsNote = async (existing, label) => {
    const entry = { id: 'pn_' + Math.random().toString(36).slice(2, 8), date: new Date().toISOString().slice(0, 10), text: '', label: label.slice(0, 24) };
    await updatePitch(existing.id, { note_entries: [entry, ...(existing.note_entries || [])] });
    return { ...existing, note_entries: [entry, ...(existing.note_entries || [])] };
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const fallbackTitle = title || 'new pitch';
      const todayIso = new Date().toISOString().slice(0, 10);
      let created;
      let dupCount = 0;
      if (talentIds.length > 1 && !titleTouched) {
        // Untouched multi-talent title — give each pitch its own "Brand × That Talent" name.
        const all = [];
        for (const tid of talentIds) {
          const tName = talent.find((t) => t.user_id === tid)?.name;
          const perTitle = selectedBrand && tName ? `${selectedBrand.name} × ${tName}` : fallbackTitle;
          const existing = findExisting(tid);
          if (existing) {
            all.push(await logAsNote(existing, perTitle));
            dupCount += 1;
          } else {
            const createdOne = await addPitch({
              title: perTitle, talent_id: tid, talent_ids: [tid], brand_id: brandId, contact_id: contactId || null,
              status: 'pitched', pitched_date: todayIso,
            });
            all.push(createdOne);
          }
        }
        created = all[0];
      } else {
        const tid = talentIds[0] || null;
        const existing = tid ? findExisting(tid) : null;
        if (existing) {
          created = await logAsNote(existing, fallbackTitle);
          dupCount = 1;
        } else {
          created = await addPitch({
            title: fallbackTitle,
            talent_id: tid,
            talent_ids: talentIds,
            brand_id: brandId,
            contact_id: contactId || null,
            status: 'pitched',
            pitched_date: todayIso,
          });
        }
      }
      if (dupCount > 0) toast.success(`${selectedBrand?.name || 'this brand'} already has a pitch — logged as a note instead`);
      onOpenChange(false);
      if (onSaved) onSaved(created);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add pitch');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>new pitch</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">
          {defaultTitle ? <>turning <span className="font-semibold text-slate-700">"{defaultTitle}"</span> into a pitch — pick brand & talent below.</> : 'just pick brand & contact — status, notes and dates can be tweaked whenever suits.'}
        </p>
        <div className="space-y-4 mt-3">
          <div>
            <Label>Roster</Label>
            <div className="mt-1">
              <PitchTalentPicker talent={talent} selectedIds={talentIds} onChange={setTalentIds} />
            </div>
          </div>
          <div>
            <Label>Brand</Label>
            <BrandSelect value={brandId} onChange={(v) => { setBrandId(v); setContactId(''); }} placeholder="Search or pick brand…" className="mt-1" />
          </div>
          <div>
            <Label>Brand contact</Label>
            <div className="mt-1">
              <ContactSelect brand={selectedBrand} value={contactId} onChange={setContactId} />
            </div>
          </div>
          <div>
            <Label>Pitch name</Label>
            <Input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }}
              placeholder="brand × talent"
              className="mt-1"
              data-testid="quick-add-pitch-title"
            />
          </div>
        </div>
        {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={!canSave} onClick={submit} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
            {saving ? 'adding…' : 'add pitch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddPitch;

